import React, { useState, useEffect } from 'react';
import LocationManagement from './components/LocationManagement';
import AccountTypes from './components/AccountTypes';
import DocumentTypes from './components/DocumentTypes';
import TechnicalAssistanceTypes from './components/TechnicalAssistanceTypes';
import UserManagement from './components/UserManagement';
import { LocationIcon, AccountIcon, DocumentIcon, TechnicalIcon, StaffIcon, SettingsIcon, MenuIcon } from './components/Icons';

const SystemManagement = () => {
  const [selectedSection, setSelectedSection] = useState('locations');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSectionClick = (section) => {
    setSelectedSection(section);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showNotification = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const sections = [
    {
      id: 'locations',
      title: 'Location Management',
      icon: <LocationIcon className="w-5 h-5" />,
    },
    {
      id: 'accountTypes',
      title: 'Account Types',
      icon: <AccountIcon className="w-5 h-5" />,
    },
    {
      id: 'documentTypes',
      title: 'Document Types',
      icon: <DocumentIcon className="w-5 h-5" />,
    },
    {
      id: 'taTypes',
      title: 'Technical Assistance Types',
      icon: <TechnicalIcon className="w-5 h-5" />,
    },
    {
      id: 'users',
      title: 'User Management',
      icon: <StaffIcon className="w-5 h-5" />,
    },
  ];

  const renderSidebar = () => (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 flex items-center gap-2">
        <SettingsIcon className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900 font-poppins">System Settings</h3>
      </div>
      <nav className="flex-1 overflow-y-auto p-2">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => handleSectionClick(section.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all duration-200 font-poppins ${
              selectedSection === section.id
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <span className={`${selectedSection === section.id ? 'text-white' : 'text-blue-600'}`}>
              {section.icon}
            </span>
            <span className="text-sm font-medium">{section.title}</span>
          </button>
        ))}
      </nav>
    </div>
  );

  const renderMobileTabs = () => (
    <div className="sticky top-0 z-10 bg-white border-b border-gray-200 mb-4">
      <div className="flex overflow-x-auto scrollbar-hide">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => handleSectionClick(section.id)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors duration-200 whitespace-nowrap font-poppins ${
              selectedSection === section.id
                ? 'border-blue-600 text-blue-600 font-semibold'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {section.icon}
            <span className="text-xs">{section.title.split(' ')[0]}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderMobileHeader = () => (
    <div className="flex justify-between items-center mb-4 py-3 px-4 bg-white rounded-lg shadow-sm">
      <div className="flex items-center gap-2">
        <button
          onClick={handleDrawerToggle}
          className="md:hidden p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
        >
          <MenuIcon className="w-6 h-6" />
        </button>
        <h2 className="text-xl font-semibold text-gray-900 font-poppins">System Management</h2>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full min-h-[calc(100vh-160px)]">
      {/* Mobile Header */}
      {isMobile && renderMobileHeader()}
      
      <div className="flex flex-grow h-full">
        {/* Sidebar for larger screens and drawer for mobile */}
        {isMobile ? (
          <>
            {/* Mobile Drawer */}
            {mobileOpen && (
              <div className="fixed inset-0 z-50">
                <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleDrawerToggle}></div>
                <div className="absolute left-0 top-0 bottom-0 w-60 bg-white shadow-xl">
                  {renderSidebar()}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="w-64 flex-shrink-0 bg-white border-r border-gray-200 mr-4 hidden md:block">
            {renderSidebar()}
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-grow w-full md:w-[calc(100%-256px)]">
          {/* Mobile tabs for navigation */}
          {isMobile && renderMobileTabs()}
          
          {selectedSection === 'locations' && (
            <LocationManagement onSnackbarMessage={showNotification} />
          )}
          {selectedSection === 'accountTypes' && (
            <AccountTypes onSnackbarMessage={showNotification} />
          )}
          {selectedSection === 'documentTypes' && (
            <DocumentTypes onSnackbarMessage={showNotification} />
          )}
          {selectedSection === 'taTypes' && (
            <TechnicalAssistanceTypes onSnackbarMessage={showNotification} />
          )}
          {selectedSection === 'users' && (
            <UserManagement onSnackbarMessage={showNotification} />
          )}
        </div>
      </div>

      {/* Snackbar Notification */}
      {snackbar.open && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in">
          <div className={`px-6 py-4 rounded-lg shadow-lg min-w-[300px] max-w-md font-poppins text-center ${
            snackbar.severity === 'success' ? 'bg-green-500 text-white' :
            snackbar.severity === 'error' ? 'bg-red-500 text-white' :
            snackbar.severity === 'warning' ? 'bg-yellow-500 text-white' :
            'bg-blue-500 text-white'
          }`}>
            <div className="flex items-center justify-between gap-4">
              <span className="font-medium flex-1 text-center">{snackbar.message}</span>
              <button
                onClick={() => setSnackbar(prev => ({ ...prev, open: false }))}
                className="text-white hover:text-gray-200 transition-colors duration-200 flex-shrink-0"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemManagement;
